import { useMemo, useState } from "react";
import { ImageIcon } from "lucide-react";
import { asset } from "@/lib/assets";

/**
 * Media system.
 *
 * Every visual on the site goes through <Img>. While real artwork is pending it renders a
 * generated, brand-consistent placeholder that is deterministic per `seed` — so two different
 * projects never look identical (the "every case study looks the same" problem).
 *
 * To ship real imagery, put the file in `frontend/public/assets/...` and set the matching
 * `src` field in `src/data/caseStudies.json` or `src/data/site.js`. Nothing else changes.
 */

const ACCENT_FALLBACK = "#00b898";

/** Tiny deterministic string hash so a given seed always yields the same artwork. */
const hash = (str = "") => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
};

const VARIANTS = ["grid", "arcs", "bars", "orbits"];

const PlaceholderArt = ({ seed, accent }) => {
  const h = hash(seed);
  const variant = VARIANTS[h % VARIANTS.length];
  const rot = (h % 40) - 20;

  if (variant === "grid") {
    return (
      <g opacity="0.5">
        {Array.from({ length: 9 }).map((_, i) => (
          <line key={`v${i}`} x1={40 + i * 40} y1="0" x2={40 + i * 40} y2="300" stroke={accent} strokeWidth="0.6" opacity={((h >> i) % 5) / 10 + 0.1} />
        ))}
        {Array.from({ length: 6 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={40 + i * 44} x2="400" y2={40 + i * 44} stroke={accent} strokeWidth="0.6" opacity={((h >> (i + 3)) % 5) / 10 + 0.1} />
        ))}
        <rect x={60 + (h % 5) * 40} y={60 + (h % 3) * 40} width="120" height="88" fill={accent} opacity="0.16" rx="6" />
      </g>
    );
  }

  if (variant === "arcs") {
    return (
      <g transform={`rotate(${rot} 200 150)`} opacity="0.55">
        {Array.from({ length: 6 }).map((_, i) => (
          <circle key={i} cx="200" cy="150" r={40 + i * 34} fill="none" stroke={accent} strokeWidth="1" opacity={0.5 - i * 0.06} strokeDasharray={i % 2 ? "6 10" : "none"} />
        ))}
        <circle cx="200" cy="150" r="26" fill={accent} opacity="0.2" />
      </g>
    );
  }

  if (variant === "bars") {
    return (
      <g opacity="0.6">
        {Array.from({ length: 14 }).map((_, i) => {
          const bh = 30 + ((h >> i) % 160);
          return <rect key={i} x={22 + i * 26} y={260 - bh} width="14" height={bh} rx="3" fill={accent} opacity={0.12 + ((h >> (i + 2)) % 4) * 0.07} />;
        })}
      </g>
    );
  }

  return (
    <g opacity="0.55">
      <ellipse cx="200" cy="150" rx="150" ry="58" fill="none" stroke={accent} strokeWidth="1" opacity="0.35" transform={`rotate(${rot} 200 150)`} />
      <ellipse cx="200" cy="150" rx="110" ry="110" fill="none" stroke={accent} strokeWidth="1" opacity="0.22" />
      <ellipse cx="200" cy="150" rx="150" ry="58" fill="none" stroke={accent} strokeWidth="1" opacity="0.3" transform={`rotate(${rot + 62} 200 150)`} />
      {Array.from({ length: 5 }).map((_, i) => (
        <circle key={i} cx={60 + i * 70} cy={150 + (((h >> i) % 80) - 40)} r={4 + ((h >> i) % 7)} fill={accent} opacity="0.3" />
      ))}
    </g>
  );
};

/**
 * Branded stand-in shown wherever real artwork has not been supplied yet.
 * Visually deliberate — reads as a design element, not a broken image.
 */
export const MediaPlaceholder = ({ seed = "snapx", accent = ACCENT_FALLBACK, label, caption, className = "" }) => {
  const id = useMemo(() => `ph${hash(`${seed}${label || ""}`)}`, [seed, label]);
  return (
    <div
      className={`relative flex h-full w-full items-center justify-center overflow-hidden bg-ink ${className}`}
      data-testid={`media-placeholder-${seed}`}
      role="img"
      aria-label={label ? `${label} — artwork coming soon` : "Artwork coming soon"}
    >
      <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.16" />
            <stop offset="55%" stopColor="#0a0a0a" stopOpacity="1" />
            <stop offset="100%" stopColor="#050505" stopOpacity="1" />
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill={`url(#${id}g)`} />
        <PlaceholderArt seed={`${seed}${label || ""}`} accent={accent} />
      </svg>
      <div className="relative flex flex-col items-center gap-2 px-6 text-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-ink/70 backdrop-blur-sm">
          <ImageIcon className="h-4 w-4" style={{ color: accent }} />
        </span>
        {label && <p className="max-w-[22ch] text-sm font-bold leading-snug text-white/80">{label}</p>}
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">{caption || "Visual coming soon"}</p>
      </div>
    </div>
  );
};

const RATIO_CLASS = {
  "16/9": "aspect-video",
  "4/3": "aspect-[4/3]",
  "3/2": "aspect-[3/2]",
  "1/1": "aspect-square",
  "21/9": "aspect-[21/9]",
  "3/4": "aspect-[3/4]",
};

/**
 * Image with a graceful placeholder. Pass `src` once real artwork exists; omit it (or let it 404)
 * and the branded placeholder takes over. `alt` is required for accessibility + SEO.
 */
export const Img = ({
  src,
  alt = "",
  seed,
  accent = ACCENT_FALLBACK,
  ratio = "16/9",
  label,
  caption,
  className = "",
  imgClassName = "",
  priority = false,
  ...rest
}) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const showPlaceholder = !src || failed;

  return (
    <div className={`relative overflow-hidden ${RATIO_CLASS[ratio] || RATIO_CLASS["16/9"]} ${className}`} {...rest}>
      {showPlaceholder ? (
        <MediaPlaceholder seed={seed || alt || label || "snapx"} accent={accent} label={label || alt} caption={caption} />
      ) : (
        <>
          {!loaded && <div className="absolute inset-0 animate-pulse-soft bg-white/[0.04]" aria-hidden="true" />}
          <img
            src={asset(src)}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? "sync" : "async"}
            fetchPriority={priority ? "high" : undefined}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            className={`h-full w-full object-cover transition-opacity duration-700 ${loaded ? "opacity-100" : "opacity-0"} ${imgClassName}`}
          />
        </>
      )}
    </div>
  );
};

export default Img;
